import loopback from '@/services/loopback'
import router from '@/router'

export function syncToken ({commit}) {
  if (loopback.token) {
    commit('setAccessToken', loopback.token)
  }
  return Promise.resolve()
}

function clearLocalStorage (commit) {
  commit('setAccessToken', null)
  commit('setUser', null)
  commit('setOrganization', null)
  commit('setSoftswitchIntegration', null)
  commit('setFinancialData', null)
  loopback.removeToken()
  loopback.token = null
}

function evaluateRoute (state, commit, to, next) {
  return (sessionError) => {
    if (to.matched.some(record => record.meta.requiresAuth)) {
      // this route requires auth, check if logged in
      // if not, redirect to login page (except when it's profile route and
      // there is an access_token).
      if (to.name === 'change-password' && to.query.access_token) {
        next()
      } else if (!state.accessToken) {
        next({
          name: 'login',
          params: { sessionError },
          query: { redirect: to.fullPath }
        })
      } else {
        loopback.head('/users/accessTokens').then(() => {
          next()
        }).catch((err) => {
          if (err && err.response && err.response.status === 401) {
            clearLocalStorage(commit)
            next({
              name: 'login',
              query: {
                accessTokenExpired: true,
                redirect: to.fullPath
              }
            })
          } else {
            next()
          }
        })
      }
    } else {
      next() // make sure to always call next()!
    }
  }
}

export function syncRouter ({state, dispatch, commit}, myRouter) {
  myRouter.beforeEach((to, _from, next) => {
    dispatch('syncToken').then(
      evaluateRoute(state, commit, to, next)
    )
  })
}

/**
 * Sign-in account with email and password (stores in state.user)
 * @param  {Function} commit   commit mutations function
 * @param  {String}   email    user email
 * @param  {String}   password user password
 * @return {Promise}           promise of logged user
 */
export function signIn ({commit, dispatch, state}, {email, password}) {
  return loopback
    .post('/users/login', {
      email,
      password
    })
    .then((token) => {
      commit('setAccessToken', token)
      if (state.accessToken !== null) {
        loopback.setToken(state.accessToken)
        return dispatch('loadUserInfos')
      } else {
        loopback.removeToken()
      }
    })
}

/**
 * Sign-out current logged-in account
 *  (assumes that current state.user is not null)
 * @param  {Function} commit commit mutations function
 * @return {Promise}         promise of the logout
 */
export function signOut ({commit, state}) {
  return loopback
    .post('/users/logout', {
      access_token: state.accessToken.id
    })
    .then(_ => {
      clearLocalStorage(commit)
      return router.push({name: 'login'})
    })
}

/**
 * Load an account by userId in state.user
 * @param  {Function} commit    commit mutations function
 * @param  {Number}   userId account id
 * @return {Promise}            promise of the account
 */
export function loadUserInfos ({commit}) {
  return new Promise(async (resolve, reject) => {
    const user = await loopback.get(`/users/me`).catch((err) => {
      if (err.statusCode !== 429) loopback.removeToken()
      return reject(err)
    })
    if (user) commit('setUser', user)
    const organization = await loopback.get(`/accounts`).catch((err) => {
      if (err.statusCode !== 429) loopback.removeToken()
      return reject(err)
    })
    if (organization) commit('setOrganization', organization)

    await loadSoftswitchIntegration({commit})

    if (user && user.role === 'ADMIN') {
      const financialData = await loopback.get('/accounts/financial').catch((err) => {
        if (err.statusCode !== 429) loopback.removeToken()
        return reject(err)
      })
      if (financialData) commit('setFinancialData', financialData)
    }
    return resolve()
  })
}

/**
 * Load Softswitch Integration
 * @param  {Function} commit    commit mutations function
 * @return {Promise}            promise of the account
 */
export function loadSoftswitchIntegration ({commit}) {
  return loopback
    .get(`/organization_integrations?filter[where][gateway]=sippulse`)
    .then((integrations) => {
      if (integrations.length > 0) commit('setSoftswitchIntegration', integrations.pop())
    })
    .catch((err) => {
      if (err.statusCode !== 429) loopback.removeToken()
    })
}

/**
 * Change the password of the current logged-in account
 *  (assumes that current state.user is not null)
 *  (assumes that current state.accessToken is not null)
 * @param  {Function} commit       commit mutations function
 * @param  {Object}   state        Vuex state
 * @param  {String}   oldPassword  old password
 * @param  {String}   newPassword  new password
 * @return {Promise}              promise of the password reset
 */
export function changePassword (ctx, {oldPassword, newPassword}) {
  return loopback
    .post(
      '/users/change-password',
      {oldPassword, newPassword}
    )
}

/**
 * Reset the password of the current logged-in account
 *  (assumes that current state.user is not null)
 *  (assumes that current state.accessToken is not null)
 * @param  {Function} commit       commit mutations function
 * @param  {Object}   state        Vuex state
 * @param  {String}   oldPassword  old password
 * @param  {String}   newPassword  new password
 * @return {Promise}              promise of the password reset
 */
export function resetPassword (ctx, {newPassword, accessToken}) {
  return loopback
    .post(
      `/users/reset-password?access_token=${accessToken}`,
      {newPassword}
    )
}

/**
 * Send a email to the account user to reset the password
 * @param  {Function} commit       commit mutations function
 * @param  {String}   email        user email
 * @return {Promise}               promise of the sent email
 */
export function rememberPassword (ctx, email) {
  return loopback
    .post('/users/reset', {email})
}

/**
 * Signup account with domain, password, email, domain and phone
 * @param  {Function} commit          commit mutations function
 * @param  {String}   password        user password
 * @param  {String}   email           user email
 * @param  {String}   domain          user domain
 * @param  {Number}   phone           user phone
 * @param  {Boolean}  emailVerified   account email verified
 * @param  {String}   voucherCode     voucher with credit
 * @return {Promise}                  promise of logged user
 */
export function signUp (ctx, {organizationName, domain, password, email, phone, emailVerified, voucherCode, crm, redirect, userQuantity, campaignId}) {
  return loopback
    .post('/accounts/signup', {
      'organization_name': organizationName,
      'domain': domain,
      'password': password,
      'email': email,
      'phone': phone,
      'email_verified': emailVerified,
      'crm': crm,
      'voucher': voucherCode,
      'redirect': redirect,
      'user_quantity': userQuantity,
      'campaign_id': campaignId
    })
    .catch((err) => {
      return Promise.reject(err)
    })
}



// WEBPACK FOOTER //
// ./src/store/modules/auth/actions.js